import { type MergedCoupon } from '@cart-services/types'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useTimeout } from '@dni-react-hooks/use-timeout'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownLarge, ArrowUpLarge } from '@pets-ui/icons'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { CouponConditionList } from '../CouponConditionList/CouponConditionList'
import { CouponImage } from '../CouponImage/CouponImage'
import { CouponProductSelector, type CouponSpecificProductsMapItem } from '../CouponProductSelector/CouponProductSelector'
import { type CouponProductToggleVariationHandler } from '../CouponProductSelector/CouponProductSelectorItem'
import { useContextPromotionCouponList } from './PromotionCouponListProvider'

const styles = stylex.create({
  root: {
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 2,
    transitionDuration: '0.3s',
    transitionProperty: 'border-color',
    transitionTimingFunction: 'ease-in-out',
  },
  focused: {
    borderColor: colors.yellow600,
  },
  button: {
    display: 'flex',
  },
})

export interface PromotionCouponListItemProps {
  coupon: MergedCoupon
  couponSpecificProductsMapItem?: CouponSpecificProductsMapItem
  onToggleCouponProducts?: (isOpen: boolean) => void
  onToggleCouponProduct?: CouponProductToggleVariationHandler
}

export const PromotionCouponListItem = (props: PromotionCouponListItemProps) => {
  const { coupon, couponSpecificProductsMapItem, onToggleCouponProducts, onToggleCouponProduct } = props
  const { focusCouponId, onFocusEnd, scrollMarginTop } = useContextPromotionCouponList()
  const [isOpen, setIsOpen] = useState(true)
  const [isStartFocus, setIsStartFocus] = useState(false)
  const focused = focusCouponId === coupon.coupon_id
  const paperRef = useRef<HTMLDivElement>(null)

  const handleToggle = useCallbackEvent(() => {
    setIsOpen(!isOpen)
    onToggleCouponProducts?.(!isOpen)
  })

  const hasProductGroups = couponSpecificProductsMapItem && couponSpecificProductsMapItem.product_groups.length > 0

  useEffect(() => {
    if (!focused) return

    const scrollToFocusedCoupon = () => {
      paperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setIsStartFocus(true)
      setIsOpen(true)
      onFocusEnd?.()
    }

    const promotionTabContainer = document.getElementById('promotion-tab-container')
    if (!promotionTabContainer) return

    /** 原本就在 promotion tab 時，直接進行 focus */
    const isTabActive = promotionTabContainer.offsetParent !== null
    if (isTabActive) {
      scrollToFocusedCoupon()
      return
    }

    /**
     * 在其他 tab 時，監聽 promotion tab class 變化時（從 display: none 變為 display: block），進行 focus
     */
    const observer = new MutationObserver(() => {
      const promotionOffsetParent = promotionTabContainer.offsetParent
      if (promotionOffsetParent !== null) {
        scrollToFocusedCoupon()
        observer.disconnect()
      }
    })

    observer.observe(promotionTabContainer, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => observer.disconnect()
  }, [focused, onFocusEnd])

  useTimeout(() => {
    setIsStartFocus(false)
  }, isStartFocus ? 3000 : undefined)

  return (
    <Paper
      rootStyle={[
        styles.root,
        isStartFocus && styles.focused,
      ]}
      rootRef={paperRef}
      p={3}
      borderRadius={8}
      scrollMarginTop={scrollMarginTop}
      id={`${coupon.coupon_id}`}
    >
      <VStack gap={2}>
        <ButtonBase onClick={handleToggle} rootStyle={styles.button} disableRipple>
          <HStack gap={2.5} alignItems="center" flexWrap="nowrap" width="100%">
            <CouponImage
              criterionGroups={coupon.criterion_groups}
              imageUrl={coupon.thumbnail_url || ''}
            />
            <VStack gap={1}>
              <Text variant="subtitle">{coupon.display_name}</Text>
              <Text variant="caption" color={colors.grey500}>
                使用期限：
                {coupon.end_time ? format(new Date(coupon.end_time), 'yyyy-MM-dd HH:mm') : '無期限'}
              </Text>
            </VStack>
            {hasProductGroups
            && (
              <>
                <Box flexGrow={1} />
                <Button component="div" variant="text" color="grey" size="small" flexShrink={0} endIcon={isOpen ? <ArrowUpLarge /> : <ArrowDownLarge />}>
                  挑選
                </Button>
              </>
            )}
          </HStack>
        </ButtonBase>
        {coupon.criterions.length > 0
        && (
          <Box backgroundColor={colors.grey50} p={1} borderRadius={4}>
            <CouponConditionList couponCriterions={coupon.criterions} />
          </Box>
        )}
        {hasProductGroups
        && (
          <Collapse in={isOpen}>
            <CouponProductSelector
              couponSpecificProductsMapItem={couponSpecificProductsMapItem}
              onToggleCouponProduct={onToggleCouponProduct}
            />
          </Collapse>
        )}
      </VStack>
    </Paper>
  )
}
